import { ModalAction } from '../ModalAction'
import { getModalDraft, saveModalDraft } from '@/core/api/modalDraft'
import { currentAutosaveScope } from '@/core/api/autosaveScope'
import { useAutosave } from '@/core/hooks/useAutosave'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { ecnForm } from '../../../templates'
import { updateFormField, initialFormValues, type FormValues } from '@/core/components/form/formModel'
import { FormRenderer, type TextInputRules } from '@/core/components/form/FormRenderer'
import { todayIso, nowHhMm } from '@/core/lib/dates'
import { gateAdvance } from '@/core/lib/advanceGate'
import { ApiError, captureWriteIdentity } from '@/core/api/http'
import { generateDocument } from '@/core/api/documents'
import { me } from '@/core/data/session'
import {
  checkEcnSitzung,
  ecnManualFields,
  ecnEntryFromForm,
  ecnEntryToFormValues,
  createEcnSitzung,
  updateEcnSitzung,
  type EcnSitzungFields,
} from '../../../flows/ecn'
import { getSitzung } from '../../../data/sitzungen'
import { buildDurchfuehrungsort } from '../../../lib/fillTb'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { getOrganisation } from '@/core/data/organisation'
import { getEcnStandardUe } from '../../../data/organisationEcnSettings'
import { listCoachingModuln } from '../../../data/modul'
import { listAnwesenheit } from '../../../data/anwesenheit'
import { listNachweisDocs, publishNachweis, type NachweisDocRow } from '../../../data/nachweise'
import { mergeMassnahmeOptions } from '@/core/lib/massnahmeOptions'
import { getAssignedCoaches } from '../../../data/coaching'
import { getAssignedCoach, getCoachingMassnahmeId } from '@/core/data/coachings'
import { loadMemberSignatur } from '@/core/data/signingAssets'
import { PdfRegion } from '@/core/components/modal/PdfRegion'
import { createParticipantLink } from '@/core/api/portal'
import { ModalHeader } from '@/core/components/modal/ModalHeader'
import { ScrollBar, toastError, toastSuccess } from 'admination-react'

const NACHWEIS_SIGNED_CONFLICT_TOAST =
  "Dieser Nachweis wurde inzwischen unterschrieben und kann nicht mehr bearbeitet werden. Ihre Eingaben bleiben erhalten."

const COACH_SIGNATURE_MISSING_TOAST =
  "Für den zugewiesenen Coach ist noch keine Unterschrift hinterlegt. Der Coach muss sie zuerst in den Einstellungen unter „Meine Unterschrift“ hochladen."

function ecnRefusalMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return
  const body = error.detail
  if (!body || typeof body !== 'object' || !('detail' in body)) return
  const detail = body.detail
  if (!detail || typeof detail !== 'object' || !('code' in detail)) return
  const { code } = detail
  const render = error.path === '/documents/ecn'
  const session = /^\/azav\/sitzung\/[^/]+$/.test(error.path)
  const draft = /^\/ecn\/[^/]+\/entwurf$/.test(error.path)
  if (error.status === 409 && code === 'ecn_signed' && (render || session || draft)) {
    return NACHWEIS_SIGNED_CONFLICT_TOAST
  }
  if (error.status === 412 && code === 'ecn_stale' && render) {
    return "Der Nachweis wurde inzwischen geändert. Bitte laden Sie ihn erneut. Ihre Eingaben bleiben erhalten."
  }
}

const ANWESENHEIT_DEFAULT = "Anwesend"

function blankValues(ueDefault: number, ort: string): FormValues {
  return initialFormValues(ecnForm, {
    Sitzung: {
      Datum: todayIso(),
      Beginn: nowHhMm(),
      'Dauer in UE': String(ueDefault),
      Anwesenheit: ANWESENHEIT_DEFAULT,
      Ort: ort,
    },
  })
}

const ECN_INPUT_RULES: TextInputRules = new Map([
  ['Sitzung.Beginn', { filter: /[0-9:]/, inputMode: 'numeric' as const, maxLength: 5 }],
])

function currentDoc(docs: NachweisDocRow[]): NachweisDocRow | null {
  const unsigned = docs.filter((d) => !d.signed)
  if (unsigned.length === 0) return null
  return unsigned.reduce((latest, d) => (d.createdAt > latest.createdAt ? d : latest))
}

export function EcnNachweis({ coachingId }: { coachingId: string | null }) {
  const { dispatch, reloadDashboard, coachings } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const [view, setView] = useState<'form' | 'doc'>('form')
  const landedFor = useRef<string | null>(null)
  const [values, setValues] = useState<FormValues>(() => blankValues(2, ''))
  const formFor = useRef<string | null | undefined>(undefined)
  const editedFields = useRef(new Set<string>())
  const [editing, setEditing] = useState<string | null>(null)
  const coaching = useMemo(() => coachings.find((c) => c.id === coachingId) ?? null, [coachings, coachingId])
  const budgetFull =
    coaching != null && coaching.ueGesamt != null && coaching.ueStattgefunden >= coaching.ueGesamt
  const [docs, setDocs] = useState<NachweisDocRow[]>([])
  const [busy, setBusy] = useState(false)
  const [isOwner, setIsOwner] = useState(false)
  const [myUserId, setMyUserId] = useState<string | null>(null)
  const [isAssignedCoach, setIsAssignedCoach] = useState(false)
  const [ueDefault, setUeDefault] = useState(2)
  const [orgOrt, setOrgOrt] = useState('')
  const [moduln, setModuln] = useState<{ id: string; name: string }[] | null>(null)
  const [anwesenheit, setAnwesenheit] = useState<string[] | null>(null)
  const [rendering, setRendering] = useState(false)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])
  const coachingIdRef = useRef(coachingId)
  useEffect(() => {
    coachingIdRef.current = coachingId
  }, [coachingId])

  useEffect(() => {
    me()
      .then((m) => {
        setIsOwner(m.isOwner)
        setMyUserId(m.userId)
        return Promise.all([
          getEcnStandardUe(m.organisationId).catch((e: unknown) => {
            console.error("ecn_standard_ue laden fehlgeschlagen:", e)
            return 2
          }),
          getOrganisation(m.organisationId).catch((e: unknown) => {
            console.error("Organisationsdaten laden fehlgeschlagen:", e)
            return null
          }),
        ])
      })
      .then(([ue, org]) => {
        setUeDefault(ue)
        setOrgOrt(buildDurchfuehrungsort(org))
      })
      .catch((e) => console.error("me() failed:", e))
  }, [])

  useEffect(() => {
    let cancelled = false
    listAnwesenheit()
      .then((rows) => {
        if (!cancelled) setAnwesenheit(rows.length > 0 ? rows.map((r) => r.name) : null)
      })
      .catch((e) => {
        console.error("Anwesenheit-Vokabular laden fehlgeschlagen:", e)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    setModuln(null)
    if (!coachingId) {
      setModuln([])
      return
    }
    let cancelled = false
    getCoachingMassnahmeId(coachingId)
      .then((massnahmeId) => listCoachingModuln(massnahmeId))
      .then((rows) => {
        if (!cancelled) setModuln(rows)
      })
      .catch((e) => {
        console.error("Module der Maßnahme laden fehlgeschlagen:", e)
        if (!cancelled) setModuln([])
      })
    return () => {
      cancelled = true
    }
  }, [coachingId])

  const [docsLoaded, setDocsLoaded] = useState(false)
  const reload = useCallback(() => {
    const scope = currentAutosaveScope()
    const isCurrent = () => alive.current && coachingIdRef.current === coachingId && currentAutosaveScope() === scope
    if (!isCurrent()) return
    if (!coachingId) {
      setDocs([])
      setDocsLoaded(true)
      return
    }
    listNachweisDocs(coachingId)
      .then((d) => {
        if (!isCurrent()) return
        setDocs(d)
        setDocsLoaded(true)
      })
      .catch((e) => {
        if (!isCurrent()) return
        console.error("Nachweis-Dokumente laden fehlgeschlagen:", e)
        setDocsLoaded(true)
      })
  }, [coachingId])

  useEffect(() => {
    if (formFor.current !== coachingId) {
      formFor.current = coachingId
      editedFields.current.clear()
      setValues(blankValues(ueDefault, orgOrt))
      setEditing(null)
      reload()
      return
    }
    if (editing !== null) return
    setValues((previous) => {
      const session = previous.Sitzung ?? {}
      const defaults = { 'Dauer in UE': String(ueDefault), Ort: orgOrt }
      const updates = Object.fromEntries(Object.entries(defaults).filter(([field, value]) =>
        !editedFields.current.has(`Sitzung.${field}`) && session[field] !== value,
      ))
      return Object.keys(updates).length ? { ...previous, Sitzung: { ...session, ...updates } } : previous
    })
  }, [coachingId, reload, ueDefault, orgOrt, editing])

  useEffect(() => {
    if (budgetFull && editing === null && !busy) setView('doc')
  }, [budgetFull, editing, busy])

  useEffect(() => {
    setView('form')
    setDocsLoaded(false)
    setLandedOnPending(false)
    landedFor.current = null
  }, [coachingId])

  const [landedOnPending, setLandedOnPending] = useState(false)
  useEffect(() => {
    if (!coachingId || !docsLoaded || landedFor.current === coachingId) return
    landedFor.current = coachingId
    if (currentDoc(docs)) {
      setView('doc')
      setLandedOnPending(true)
    }
  }, [coachingId, docsLoaded, docs])

  useEffect(() => {
    if (!coachingId || !myUserId) {
      setIsAssignedCoach(false)
      return
    }
    let cancelled = false
    getAssignedCoaches(coachingId)
      .then((coaches) => {
        if (!cancelled) setIsAssignedCoach(coaches.some((coach) => coach.id === myUserId))
      })
      .catch((e) => {
        console.error("Zugewiesener Coach konnte nicht ermittelt werden:", e)
        if (!cancelled) setIsAssignedCoach(false)
      })
    return () => {
      cancelled = true
    }
  }, [coachingId, myUserId])

  const setField = useCallback((path: string, v: unknown) => {
    if (busy) return
    editedFields.current.add(path)
    setValues((prev) => updateFormField(prev, path, v))
  }, [busy])

  const check = useMemo(() => checkEcnSitzung(values), [values])

  const modulValue = values.Sitzung ? String(values.Sitzung['Modul'] ?? '').trim() : ''
  const anwesenheitValue = values.Sitzung ? String(values.Sitzung['Anwesenheit'] ?? '').trim() : ''
  const optionsFor = useCallback(
    (path: string, templateOptions: string[]) => {
      if (path === 'Sitzung.Modul') {
        return mergeMassnahmeOptions(moduln?.map((m) => m.id) ?? null, templateOptions, modulValue)
      }
      if (path === 'Sitzung.Anwesenheit') {
        return mergeMassnahmeOptions(anwesenheit, templateOptions, anwesenheitValue)
      }
      return templateOptions
    },
    [moduln, modulValue, anwesenheit, anwesenheitValue],
  )

  const modulNameById = useMemo(
    () => new Map((moduln ?? []).map((m) => [m.id, m.name])),
    [moduln],
  )
  const optionLabel = useCallback(
    (path: string, option: string) =>
      path === 'Sitzung.Modul' ? (modulNameById.get(option) ?? option) : option,
    [modulNameById],
  )

  const selectedDoc = useMemo(() => currentDoc(docs), [docs])

  const close = () => dispatch({ type: 'CLOSE' })

  const { flush: flushDraft, beginLoad: beginDraftLoad } = useAutosave(values, editing !== null && !busy, (value) => {
    if (!editing) return
    return saveModalDraft(`/ecn/${editing}/entwurf`, value)
  }, 250, { form: `ecn:${editing ?? selectedDoc?.sitzungId ?? 'new'}`, record: coachingId ?? undefined, label: "Einzelcoachingnachweis", recover: setValues })

  function confirm() {
    if (!coachingId || busy) return
    if (budgetFull && editing === null) return
    const identity = captureWriteIdentity()
    const isCurrent = () => alive.current && coachingIdRef.current === coachingId && currentAutosaveScope() === identity.scope
    let creatingDocument = false
    setBusy(true)
    ;(async (): Promise<'success' | 'signedConflict' | 'coachSignatureMissing' | void> => {
      if (editing !== null) await flushDraft()
      const coach = await getAssignedCoach(coachingId)
      if (coach && !(await loadMemberSignatur(coach.id))) return 'coachSignatureMissing'
      if (!isCurrent()) return
      const fields = ecnEntryFromForm(values)
      let sitzungId: string
      if (editing !== null) {
        const currentDocs = await listNachweisDocs(coachingId)
        if (!isCurrent()) return
        if (currentDocs.find((d) => d.sitzungId === editing)?.signed) {
          return 'signedConflict'
        }
        await updateEcnSitzung(editing, fields, identity)
        sitzungId = editing
      } else {
        sitzungId = await createEcnSitzung(coachingId, fields, identity)
        if (!isCurrent()) return
        setEditing(sitzungId)
      }
      if (!isCurrent()) return
      setRendering(true)
      creatingDocument = true
      await generateDocument('ecn', { coaching_id: coachingId, sitzung_id: sitzungId }, identity)
      if (!isCurrent()) return
      return 'success'
    })()
      .then((outcome) => {
        if (!isCurrent() || !outcome) return
        if (outcome === 'coachSignatureMissing') {
          toastError(COACH_SIGNATURE_MISSING_TOAST)
          return
        }
        if (outcome === 'signedConflict') {
          toastError(NACHWEIS_SIGNED_CONFLICT_TOAST)
          reload()
          return
        }
        toastSuccess(editing !== null ? "Sitzung aktualisiert." : "Sitzung gespeichert.")
        reloadDashboard()
        reload()
        editedFields.current.clear()
        setValues(blankValues(ueDefault, orgOrt))
        setEditing(null)
        setView('doc')
      })
      .catch((e) => {
        if (!isCurrent()) return
        console.error("Sitzung konnte nicht gespeichert werden:", e)
        const refusal = ecnRefusalMessage(e)
        if (refusal) {
          toastError(refusal)
          reload()
          return
        }
        toastError(friendlyErrorMessage(e, creatingDocument
          ? "Nachweis-PDF konnte nicht erstellt werden."
          : "Sitzung konnte nicht gespeichert werden - bitte erneut versuchen."))
      })
      .finally(() => {
        if (isCurrent()) {
          setBusy(false)
          setRendering(false)
        }
      })
  }

  function startEdit() {
    if (!coachingId || busy || !selectedDoc || !selectedDoc.sitzungId) return
    beginDraftLoad()
    setBusy(true)
    const requestedCoachingId = coachingId
    getSitzung(selectedDoc.sitzungId)
      .then(async (row) => {
        if (!alive.current || coachingIdRef.current !== requestedCoachingId) return
        if (!row) {
          toastError("Sitzung konnte nicht geladen werden — bitte erneut versuchen.")
          return
        }
        const entry: EcnSitzungFields = {
          datum: row.datum,
          beginn: row.beginn,
          ue: row.ue,
          anwesenheit: row.anwesenheit,
          inhalt: row.inhalt,
          ort: row.ort,
          modulId: row.modulId,
        }
        const entwurf = await getModalDraft<FormValues>(`/ecn/${row.id}/entwurf`)
        if (!alive.current || coachingIdRef.current !== requestedCoachingId) return
        setValues(entwurf ?? ecnEntryToFormValues(entry))
        setEditing(row.id)
        setView('form')
      })
      .catch((e) => {
        if (!alive.current || coachingIdRef.current !== requestedCoachingId) return
        console.error("Sitzung zum Bearbeiten laden fehlgeschlagen:", e)
        toastError("Sitzung konnte nicht geladen werden — bitte erneut versuchen.")
      })
      .finally(() => {
        if (alive.current) setBusy(false)
      })
  }

  const requestCountersign = () => {
    if (!coachingId || busy || !selectedDoc) return
    const identity = captureWriteIdentity()
    const isCurrent = () => alive.current && coachingIdRef.current === coachingId && currentAutosaveScope() === identity.scope
    setBusy(true)
    publishNachweis(selectedDoc.id, identity)
      .then(() => createParticipantLink(coachingId, 'nachweis', landedOnPending, identity))
      .then((link) => {
        if (!isCurrent()) return
        toastSuccess(`Unterschrift-Anfrage an ${link.sent_to} gesendet.`)
        reloadDashboard()
        dispatch({ type: 'CLOSE_FOR', coachingId })
      })
      .catch((e) => {
        if (!isCurrent()) return
        console.error("Unterschrift-Anfrage konnte nicht gesendet werden:", e)
        toastError(friendlyErrorMessage(e, "Unterschrift-Anfrage konnte nicht gesendet werden."))
      })
      .finally(() => {
        if (isCurrent()) setBusy(false)
      })
  }

  return (
    <>
      <ModalHeader size={view === 'doc' ? 'document' : 'form'} onClose={close} closeDisabled={busy}>
        <div className="pill-cluster">
          {view === 'doc' ? (
            <>
              {coachingId && selectedDoc && !selectedDoc.signed && selectedDoc.sitzungId && (
                <ModalAction
                  icon="i-edit"
                  title="Bearbeiten"
                  disabled={busy}
                  onClick={startEdit}
                />
              )}
              {(isOwner || isAssignedCoach) && coachingId && selectedDoc && (
                <ModalAction
                  icon="i-send"
                  primaryAction
                  loading={busy}
                  title={
                    landedOnPending
                      ? "Erinnerung an Teilnehmenden senden"
                      : "Link an Teilnehmenden senden"
                  }
                  disabled={busy}
                  onClick={requestCountersign}
                />
              )}
            </>
          ) : (
            <>
              {!(budgetFull && editing === null) && (
                <ModalAction
                  icon="i-doc"
                  primaryAction
                  title="Dokument erstellen"
                  loading={busy}
                  disabled={busy}
                  onClick={() => gateAdvance(check, confirm)}
                />
              )}

            </>
          )}
        </div>
      </ModalHeader>
      {view === 'doc' ? (
        <PdfRegion
          path={selectedDoc?.bucketAddress || null}
          waiting={rendering}
          loadingLabel={rendering ? "Einzelnachweis wird erstellt …" : "Einzelnachweis wird geladen …"}
          placeholder="Für diese Sitzung wird der Einzelnachweis noch erstellt."
        />
      ) : (
        <>
          <main className="form" data-scroll-host ref={scrollerRef}>
            <fieldset disabled={busy} className="form-contents"><FormRenderer
                definition={ecnForm}
                values={values}
                onChange={setField}
                errors={check.errors}
                field={(field) => ({
                  required: ecnManualFields?.has(field.id),
                  missing: ecnManualFields?.has(field.id) ? 'mark' : undefined,
                  ...ECN_INPUT_RULES?.get(field.id),
                  options: field.control === 'select' ? optionsFor(field.id, field.options ?? []) : undefined,
                  optionLabel: field.control === 'select' ? (option: string) => optionLabel(field.id, option) : undefined,
                })}
              /></fieldset>
          </main>
          <ScrollBar scrollerRef={scrollerRef} />
        </>
      )}
    </>
  )
}
